import { LocalDate, Month } from "@js-joda/core";
import h from "trader-hyperscript";
import { Source } from "trader.ts/types/source";
import { dynClass, dynVariadic, scheduleForCleanup } from "trader.ts/ui";
import * as style from "../../styles/style.css.classes";
import { holiday } from "../domain/periods";
import {
  getMonthsFromDateRange,
  isContained,
  makeDateRange,
} from "../lib/time";
import {
  makeAndTrackSmallLayoutS,
  renderFloatingPopup,
} from "../pages/planning/floatingpopup";
import { translateMonth } from "../translations_specific";
import { chunk } from "../lib/chunk";
import { isNil } from "../lib/istype";

export function renderMonth(opts: {
  month: Month;
  year: number;
  renderCell: (d: LocalDate) => HTMLElement;
}): HTMLElement {
  const firstday = LocalDate.of(opts.year, opts.month, 1);
  const lastday = LocalDate.of(opts.year, opts.month, 1)
    .plusMonths(1)
    .minusDays(1);
  const dates = makeDateRange(firstday, lastday);
  const emptySlotsBefore = firstday.dayOfWeek().ordinal();
  /* eslint-disable */
  const weeks: (undefined | LocalDate)[][] = chunk(
    [...Array(emptySlotsBefore), ...dates],
    7,
  );
  /* eslint-enable */
  return (
    <div className={style.yearcalendar_month}>
      <table>
        {weeks.map((week) => (
          <tr>
            {week.map((d) => (isNil(d) ? <td></td> : opts.renderCell(d)))}
          </tr>
        ))}
      </table>
    </div>
  );
}

export function renderMonthsWithSelection(opts: {
  period: { firstday: LocalDate; lastday: LocalDate };
  nextPeriod: { firstday: LocalDate; lastday: LocalDate } | null;
  lastday: LocalDate;
  holidays: holiday[];
  today: LocalDate;
  makeLink: (selected: null | LocalDate) => string;
  selectedS: Source<null | LocalDate>;
  canSelect: (d: LocalDate) => boolean;
  saveLinkIntoHistory: boolean;
  renderCell: (
    d: LocalDate,
    holiday: null | holiday,
  ) => {
    className: string;
    style: string;
    contents: null | string | HTMLElement;
  };
  renderPopupContents: (d: LocalDate, holiday: null | holiday) => HTMLElement;
}) {
  const months = getMonthsFromDateRange(opts.period.firstday, opts.lastday);

  const spacer = () => <div style="flex: 1"></div>;

  const monthsRendered = chunk(months, 3).map((monthsGrouped) => {
    return (
      <div className={style.flex_responsive}>
        {monthsGrouped.map((month) => {
          const m = renderMonth({
            year: month[0],
            month: month[1],
            renderCell: function (d: LocalDate): HTMLElement {
              const holiday =
                opts.holidays.find((h) => isContained(d, h)) || null;
              const dow = d.dayOfWeek();
              const rendered = opts.renderCell(d, holiday);
              const isOutsidePeriod =
                // Before first period
                d.isBefore(opts.period.firstday) ||
                (d.isAfter(opts.period.lastday) &&
                  // After period
                  (opts.nextPeriod === null ||
                    // In between periods
                    d.isBefore(opts.nextPeriod.firstday) ||
                    // After next period
                    d.isAfter(opts.nextPeriod.lastday)));

              return dynClass(
                opts.selectedS,
                (selected) =>
                  selected === null
                    ? ""
                    : selected.isEqual(d)
                    ? style.yearcalendar_day_selected
                    : "",
                <td
                  id={d.toEpochDay().toString()}
                  style={opts.canSelect(d) ? "" : "pointer-events: none;"}
                  onclick={() => {
                    goToLink(opts.makeLink(d), opts);
                    if (window.innerWidth <= 768) {
                      setTimeout(() => {
                        const el = document.getElementById(
                          d.toEpochDay().toString(),
                        );
                        if (el) {
                          el.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                          });
                        }
                      }, 10);
                    }
                  }}
                  className={
                    rendered.className +
                    style.yearcalendar_day +
                    (holiday ? style.yearcalendar_day_outside_period : "") +
                    (isOutsidePeriod
                      ? style.yearcalendar_day_outside_period
                      : "") +
                    (d.isEqual(opts.today)
                      ? style.yearcalendar_day_today
                      : "") +
                    (!holiday && !isOutsidePeriod && dow.ordinal() >= 5
                      ? style.yearcalendar_day_weekend
                      : "")
                  }
                >
                  <div
                    className={style.yearcalendar_day_background}
                    style={rendered.style}
                  >
                    {rendered.contents === null
                      ? d.dayOfMonth()
                      : rendered.contents}
                  </div>
                </td>,
              );
            },
          });
          return (
            <div style="flex: 1">
              <h1>{translateMonth(month[1]) + " " + month[0]}</h1>
              {m}
            </div>
          );
        })}
        {monthsGrouped.length === 1
          ? [spacer(), spacer()]
          : monthsGrouped.length === 2
          ? spacer()
          : []}
      </div>
    );
  });

  const smallLayoutS = makeAndTrackSmallLayoutS({ maxWidth: 768 });
  const dummySlotsS = new Source([]);
  function recalcPopup() {
    dummySlotsS.set([]);
  }
  window.addEventListener("resize", recalcPopup);
  window.addEventListener("scroll", recalcPopup);
  scheduleForCleanup(() => {
    window.removeEventListener("resize", recalcPopup);
    window.removeEventListener("scroll", recalcPopup);
  });

  const popup = renderFloatingPopup<null | LocalDate, null, LocalDate>(
    opts.selectedS,
    dummySlotsS,
    smallLayoutS,
    (selected: null | LocalDate) => selected,
    {
      calcPopupWidth: () => 480,
      getApproximateFloatingPopupHeight: () => 200,
      renderPopupContents: (d) => {
        const holiday = opts.holidays.find((h) => isContained(d, h)) || null;
        return opts.renderPopupContents(d, holiday);
      },
      modeIsDayView: () => false,
      offsetLeft: 32,
      offsetRight: 32,
      getSelectedElementDimensions: (d: LocalDate) => {
        const el = document.getElementById(d.toEpochDay().toString());
        if (!el) {
          throw new Error("Failed to find calendar date");
        }
        return el.getBoundingClientRect();
      },
    },
  );

  const popupBackdrop = dynVariadic(
    [smallLayoutS, opts.selectedS],
    ([smallLayout, selected]) => {
      if (smallLayout === false || selected === null) {
        return <span></span>;
      }

      return (
        <div
          className={style.popup_backdrop}
          style="pointer-events: none;"
          onclick={() => {
            goToLink(opts.makeLink(null), opts);
          }}
        ></div>
      );
    },
  );
  return [...monthsRendered, popup, ...popupBackdrop];
}

export function closePopupWhenClickingOutsidePopupOnMonthPage(
  noSelectionLink: string,
  ev: MouseEvent,
  opts: {
    saveLinkIntoHistory: boolean;
  },
) {
  const targetClassName = (ev.target as HTMLElement).className;
  const parentClassName =
    (ev.target as HTMLElement).parentElement?.className || "";
  const grandparentClassName =
    (ev.target as HTMLElement).parentElement?.parentElement?.className || "";
  const grandgrandparentClassName =
    (ev.target as HTMLElement).parentElement?.parentElement?.parentElement
      ?.className || "";

  function classNameHasClassesWeLookFor(className: string) {
    return (
      className.includes(style.yearcalendar_day) ||
      className.includes(style.yearcalendar_day_background)
    );
  }
  if (
    classNameHasClassesWeLookFor(targetClassName) ||
    classNameHasClassesWeLookFor(parentClassName) ||
    classNameHasClassesWeLookFor(grandparentClassName) ||
    classNameHasClassesWeLookFor(grandgrandparentClassName)
  ) {
  } else {
    // clear selection when we click somewhere else than on a day (or the popup - handled via stopPropagation)
    goToLink(noSelectionLink, opts);
  }
}

function goToLink(
  url: string,
  opts: {
    saveLinkIntoHistory: boolean;
  },
) {
  if (opts.saveLinkIntoHistory) {
    document.location.hash = url;
  } else {
    // We don't want to add to the history stack
    history.replaceState(null, "", url);
    // But we do want to trigger the clientside router
    window.dispatchEvent(new HashChangeEvent("hashchange"));
  }
}
